import { useState, useEffect, useRef, useCallback } from 'react';

export const useTimer = (onTimeout, inactivityThreshold = 5 * 60 * 1000) => {
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [inactivityTime, setInactivityTime] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  const timerRef = useRef(null);
  const inactivityRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());
  const isWarning = time <= 5 * 60 * 1000 && time > 0;

  useEffect(() => {
    if (isActive && time > 0 && !isExpired) {
      const updateTimer = () => {
        const now = Date.now();
        const delta = now - lastUpdateRef.current;
        lastUpdateRef.current = now;

        setTime(prev => {
          const newTime = Math.max(0, prev - delta);

          if (newTime <= 0) {
            setIsExpired(true);
            setIsActive(false);
            if (onTimeout) onTimeout();
            return 0;
          }
          return newTime;
        });
      };

      timerRef.current = setInterval(updateTimer, 100);
      lastUpdateRef.current = Date.now();

      return () => {
        clearInterval(timerRef.current);
      };
    } else if (time === 0 && isActive) {
      setIsExpired(true);
      setIsActive(false);
      if (onTimeout) onTimeout();
    }
  }, [isActive, time, isExpired, onTimeout]);

  useEffect(() => {
    if (isActive && !isExpired) {
      inactivityRef.current = setInterval(() => {
        setInactivityTime(prev => prev + 1000);
      }, 1000);
    } else {
      clearInterval(inactivityRef.current);
      setInactivityTime(0);
    }

    return () => clearInterval(inactivityRef.current);
  }, [isActive, isExpired]);

  const handleInactivity = useCallback(() => {
    return inactivityTime >= inactivityThreshold && !isExpired;
  }, [inactivityTime, inactivityThreshold, isExpired]);

  const start = (customTime) => {
    if (customTime) {
      setTime(customTime);
      setInitialTime(customTime);
    }
    setIsActive(true);
    setIsExpired(false);
    setInactivityTime(0);
    lastUpdateRef.current = Date.now();
  };

  const pause = () => setIsActive(false);
  const resume = () => {
    setIsActive(true);
    lastUpdateRef.current = Date.now();
  };

  const reset = () => {
    setTime(initialTime);
    setInactivityTime(0);
    setIsActive(false);
    setIsExpired(false);
    lastUpdateRef.current = Date.now();
  };

  const resetInactivity = () => setInactivityTime(0);

  const expire = () => {
    setIsExpired(true);
    setIsActive(false);
    setTime(0);
  };

  const skipTime = (milliseconds) => {
    setTime(prev => {
      const newTime = Math.max(0, prev - milliseconds);
      if (newTime <= 0) {
        setIsExpired(true);
        setIsActive(false);
        if (onTimeout) onTimeout();
        return 0;
      }
      return newTime;
    });
  };

  const formatTime = () => {
    const totalSeconds = Math.floor(time / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (initialTime === 0) return 0;
    return ((initialTime - time) / initialTime) * 100;
  };

  const getInactivityMinutes = () => Math.floor(inactivityTime / 60000);

  return {
    time,
    formattedTime: formatTime(),
    initialTime,
    progress: getProgress(),
    isActive,
    isExpired,
    start,
    pause,
    resume,
    reset,
    resetInactivity,
    expire,
    skipTime,
    inactivityMinutes: getInactivityMinutes(),
    shouldWarn: handleInactivity(),
    timeLeft: time,
    isWarning
  };
};